import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SafeText } from "@/shared/components/SafeText";
import { MEMBER_LOCATIONS_INLINE } from "./constants";
import type { AdminOrganizationMember, OrganizationMemberLocation } from "./types";
import { userPath } from "@/shared/users/user-link";

interface MemberLocationsProps {
  readonly member: AdminOrganizationMember;
}

function LocationItem({ location }: { readonly location: OrganizationMemberLocation }) {
  const { t } = useTranslation();
  return (
    <li className={location.active ? "break-words" : "break-words text-muted-foreground"}>
      <SafeText fallback={t("organizations.members.locations.unnamed")}>{location.name}</SafeText>{" "}
      <span className="text-xs text-muted-foreground">
        ({t(`organizations.kind.${location.kind}`)}
        {location.active ? "" : `, ${t("organizations.members.locations.inactive")}`})
      </span>
    </li>
  );
}

/**
 * The locations a member is assigned to. Only STAFF are scoped to locations:
 * OWNER and ADMIN reach every one, so they read "not applicable". The API caps
 * the list at 20 per member (oldest first) and says so with
 * `locationsTruncated` + `locationsCount`; the rest is on the member's user
 * page (their memberships), which the "+N more" link opens.
 */
export function MemberLocations({ member }: MemberLocationsProps) {
  const { t } = useTranslation();
  const { membershipId, locations, locationsCount } = member;

  // Computed from the counts, not only the flag: whatever the API did not send is still there.
  const hidden = Math.max(0, locationsCount - locations.length);

  if (locations.length === 0 && hidden === 0) {
    return (
      <span data-testid={`member-no-locations-${membershipId}`} className="text-muted-foreground">
        {t(
          member.role === "STAFF"
            ? "organizations.members.locations.none"
            : "organizations.members.locations.notApplicable",
        )}
      </span>
    );
  }

  const inline = locations.slice(0, MEMBER_LOCATIONS_INLINE);
  const rest = locations.slice(MEMBER_LOCATIONS_INLINE);
  return (
    <div data-testid={`member-locations-${membershipId}`} className="space-y-1">
      <ul className="space-y-0.5">
        {inline.map((l) => (
          <LocationItem key={`${l.kind}:${l.id}`} location={l} />
        ))}
      </ul>
      {rest.length > 0 && (
        <details data-testid={`member-locations-more-${membershipId}`}>
          <summary className="cursor-pointer text-xs font-medium text-primary">
            {t("organizations.members.locations.showMore", { count: rest.length })}
          </summary>
          <ul className="mt-1 space-y-0.5">
            {rest.map((l) => (
              <LocationItem key={`${l.kind}:${l.id}`} location={l} />
            ))}
          </ul>
        </details>
      )}
      {hidden > 0 && (
        <Link
          to={userPath(member.user.id)}
          data-testid={`member-locations-truncated-${membershipId}`}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline"
        >
          {t("organizations.members.locations.moreOnUserPage", { count: hidden })}
        </Link>
      )}
    </div>
  );
}
